import {
  IGetMatchArg,
  IGetMatchesArg,
  ISoccerApiErrorResponse,
  ISoccerCommentaryMatch,
  ISoccerMatch,
  ISoccerMatches,
  ISoccerMatchLineups,
  ISoccerMatchListItem,
  ISoccerMatchPreview,
  ISoccerMatchStats,
  ISoccerStandings,
  isSoccerErrorResponse,
  SoccerMatchStatus,
} from "../../../types";
import {
  appTournamentConfig,
  ISupportedMatchShowResultUntil,
} from "../config/tournament";
import { updateMultipleLiveMatchesState } from "../utils/multipleLiveMatches";

const BASE_URL = import.meta.env.VITE_API_BASE_URL;
const GAME_CENTER_API_KEY = import.meta.env.VITE_GAME_CENTER_API_KEY;
const apiHeaders = {
  Authorization: `Bearer ${GAME_CENTER_API_KEY}`,
};
const fetchOpts: RequestInit = { headers: apiHeaders, cache: "no-store" };

export interface CacheMetricsBucket {
  lru: number;
  redis: number;
  sdapi: number;
  total: number;
}

export type CacheWindowKey = "1m" | "5m" | "1h" | "24h" | "all";

export interface CacheWindowSummary {
  totals: CacheMetricsBucket;
  endpoints: Record<string, CacheMetricsBucket>;
}

export interface CacheMetricsResponse {
  generatedAt: number;
  windows: Record<CacheWindowKey, CacheWindowSummary>;
}

export const getCacheMetrics = async (): Promise<CacheMetricsResponse> => {
  const res = await fetch(`${BASE_URL}/soccer/cache-metrics`, fetchOpts);
  return res.json();
};

export const getSoccerMatchCommentary = async ({
  matchId,
  lang,
}: {
  matchId: string;
  lang?: string;
}) => {
  const params = [`matchId=${matchId}`, `lang=${lang || "en-gb"}`];
  const res = await fetch(
    `${BASE_URL}/soccer/commentary?${params.join("&")}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerCommentaryMatch;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};

export const getMatchLineups = async (matchId: string) => {
  const res = await fetch(
    `${BASE_URL}/soccer/match-lineups?matchId=${matchId}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerMatchLineups;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};

export const getMatchPreview = async (matchId: string) => {
  const res = await fetch(
    `${BASE_URL}/soccer/match-preview?matchId=${matchId}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerMatchPreview;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};

export const getMatchStats = async (matchId: string) => {
  const res = await fetch(
    `${BASE_URL}/soccer/match-stats?matchId=${matchId}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerMatchStats;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};

export const getTournamentStats = async (tournamentId: string) => {
  const res = await fetch(
    `${BASE_URL}/soccer/tournament-stats?tournamentId=${tournamentId}`,
    fetchOpts
  );
  return res.json();
};

export const getStandings = async (tournamentId: string) => {
  const res = await fetch(
    `${BASE_URL}/soccer/standings?tournamentId=${tournamentId}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerStandings;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};

export const getMatch = async ({
  matchId,
  deltaSince,
  liveScore,
}: IGetMatchArg) => {
  const queryParams = [
    deltaSince && `deltaSince=${deltaSince}`,
    liveScore && `liveScore=${liveScore}`,
  ].filter(Boolean);

  const res = await fetch(
    `${BASE_URL}/soccer/matches/${matchId}?${queryParams.join("&")}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerMatch;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};

export const getMatches = async ({
  tournamentId,
  stageId,
  week,
  status,
  since,
  until,
  sort = "asc",
  liveScore,
  size = 1000,
  matchId,
}: IGetMatchesArg) => {
  const queryParams = [
    tournamentId && `tournamentId=${tournamentId}`,
    stageId && `stageId=${stageId}`,
    week && `week=${week}`,
    status && `status=${status}`,
    sort && `sort=${sort}`,
    liveScore && `liveScore=${liveScore}`,
    size && `size=${size}`,
    matchId && `matchId=${matchId}`,
    ...(since && until ? [`since=${since}`, `until=${until}`] : []),
  ].filter(Boolean);
  const res = await fetch(
    `${BASE_URL}/soccer/matches?${queryParams.join("&")}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerMatches | ISoccerApiErrorResponse;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};

export const getMatchStandings = async (
  tournamentId: string,
  matchId: string,
) => {
  const params = [`tournamentId=${tournamentId}`, `matchId=${matchId}`];
  const res = await fetch(
    `${BASE_URL}/soccer/standings?${params.join("&")}`,
    fetchOpts
  );
  const data = (await res.json()) as ISoccerStandings;
  if (isSoccerErrorResponse(data)) {
    throw new Error(data.error);
  }
  return data;
};
/**
 *
 * @param tournamentId
 * @returns match Id based on below condition with reference to current dateTime
 * IF there are supported Match list:
 *  1. current playing match if part of supported match list
 *  2. supported match played in last 24 hrs, showing match from match ended until showResultUntilTime UTC
 *  3. next upcoming supported match if no supported match in last 24hrs
 *  4. showing last supported match if no upcoming supported match
 * ELSE:
 *  1. current playing match, incase of multiple matches return overlapped matchId as per tounamentconfig
 *  2. match scheduled in next 3 hours, incase of multiple matches return overlapped matchId as per tounamentconfig
 *  3. final match Id in case tournament has finished
 *  4. last match played in previous 30 days or next scheduled match in next 30 days
 *    which ever is closer to current dateTime
 */
export async function getDefaultMatch(
  tournamentId: string
): Promise<ISoccerMatchListItem> {

  const tournamentConfig = appTournamentConfig[tournamentId];
  if (!tournamentConfig) {
    throw new Error(
      `Tournament config  not found for tournamentId=${tournamentId}`
    );
  }
  const {
    overlapMatchIds,
    supportedMatchIds,
    stages,
    supportedMatchShowResultUntil,
  } = tournamentConfig;
  if (supportedMatchIds.length) {
    const [playingMatches, previous1DayMatches, next90DayMatches] =
      await Promise.all([
        getMatches({
          tournamentId,
          status: SoccerMatchStatus.PLAYING,
          size: 3,
          sort: "asc",
          liveScore: true,
        }).catch(() => {
          return Promise.resolve({ matches: [] });
        }),
        // get previous 1 day matches
        getMatches({
          tournamentId,
          since: roundDateTime(
            addTime({
              time: new Date().toISOString(),
              days: -1,
            }),
            "down"
          ).toISOString(),
          until: roundDateTime(new Date(), "up").toISOString(),
          size: 10,
          sort: "desc",
        }).catch(() => {
          return Promise.resolve({ matches: [] });
        }),
        // get matches in next 90 days from current datetime
        getMatches({
          tournamentId,
          since: roundDateTime(new Date(), "down").toISOString(),
          until: roundDateTime(
            addTime({
              time: new Date().toISOString(),
              days: 90,
            }),
            "up"
          ).toISOString(),
          sort: "asc",
          size: 100,
        }).catch(() => {
          return Promise.resolve({ matches: [] });
        }),
      ]);

    // Update store state for multiple live matches
    updateMultipleLiveMatchesState(playingMatches.matches);

    if (playingMatches.matches.length) {
      const playingMatch = getSupportedMatchFromMultipleMatches(
        playingMatches.matches,
        supportedMatchIds
      );
      if (playingMatch) return playingMatch;
    }

    if (previous1DayMatches.matches.length) {
      const supportedPrevious1DayMatch = getSupportedMatchFromMultipleMatches(
        previous1DayMatches.matches,
        supportedMatchIds
      );
      if (supportedPrevious1DayMatch) {
        // Get upcoming matches to check if we should switch
        const upcomingMatches = next90DayMatches.matches ?? [];
        const nextSupportedMatch = getSupportedMatchFromMultipleMatches(
          upcomingMatches,
          supportedMatchIds
        );
        // Show last played supported match until supported time from it's match start time
        const matchToShow = getPrevious1DayMatchToShow(
          supportedPrevious1DayMatch,
          nextSupportedMatch,
          supportedMatchShowResultUntil
        );
        if (matchToShow) return matchToShow;
      }
    }

    const matchesList = next90DayMatches.matches ?? [];
    const supportedUpcomingMatch = getSupportedMatchFromMultipleMatches(
      matchesList,
      supportedMatchIds
    );

    if (supportedUpcomingMatch) return supportedUpcomingMatch;
    else {
      // return last supported match when no upcoming supported matches are there
      const lastSupportedMatch = await getMatch({
        matchId: supportedMatchIds[supportedMatchIds.length - 1],
      }).catch(() => {
        return Promise.resolve(null);
      });
      if (lastSupportedMatch) return lastSupportedMatch;
      throw new Error(`Error in fetching matches`); // In-case API fails, react-query will refetch
    }
  } else {
    const [
      playingMatches,
      next3HourMatches,
      previous1DayMatches,
      next60DayMatches,
      finalMatches,
    ] = await Promise.all([
      // get current live match
      getMatches({
        tournamentId,
        status: SoccerMatchStatus.PLAYING,
        size: 3,
        sort: "asc",
        liveScore: true,
      }).catch(() => {
        return Promise.resolve({ matches: [] });
      }),
      // get matches scheduled in next span of 3 hrs
      getMatches({
        tournamentId,
        since: roundDateTime(new Date(), "down").toISOString(),
        until: roundDateTime(
          addTime({
            time: new Date().toISOString(),
            hours: 3,
          }),
          "up"
        ).toISOString(),
        size: 3,
        sort: "asc",
      }).catch(() => {
        return Promise.resolve({ matches: [] });
      }),
      // get previous match  before current time
      getMatches({
        tournamentId,
        since: roundDateTime(
          addTime({
            time: new Date().toISOString(),
            days: -1,
          }),
          "down"
        ).toISOString(),
        until: roundDateTime(new Date(), "up").toISOString(),
        size: 1,
        sort: "desc",
      }).catch(() => {
        return Promise.resolve({ matches: [] });
      }),
      // get match scheduled in next 60 days
      getMatches({
        tournamentId,
        since: roundDateTime(new Date(), "down").toISOString(),
        until: roundDateTime(
          addTime({
            time: new Date().toISOString(),
            days: 60,
          }),
          "up"
        ).toISOString(),
        sort: "asc",
        size: 1,
      }).catch(() => {
        return Promise.resolve({ matches: [] });
      }),
      getMatches({
        tournamentId,
        stageId: stages.finalStageId,
        size: 1,
      }),
    ]);

    // Update store state for multiple live matches 
    updateMultipleLiveMatchesState(playingMatches.matches);

    if (playingMatches.matches.length) {
      return getMatchFromMultipleMatches(
        playingMatches.matches,
        overlapMatchIds
      );
    }

    if (
      finalMatches.tournament?.endDate &&
      new Date(finalMatches.tournament?.endDate).getTime() <
        new Date().getTime()
    ) {
      return finalMatches.matches[0];
    }

    if (next3HourMatches.matches.length) {
      return getMatchFromMultipleMatches(
        next3HourMatches.matches,
        overlapMatchIds
      );
    }

    const nextMatch = next60DayMatches.matches[0];
    if (!previous1DayMatches.matches.length) {
      return nextMatch;
    }

    const lastPreviousMatch = previous1DayMatches.matches[0];
    const previousMatchTimeDiff = Math.abs(
      new Date().getTime() - new Date(lastPreviousMatch.matchDateTime).getTime()
    );

    const nextMatchTimeDiff = Math.abs(
      new Date().getTime() - new Date(nextMatch.matchDateTime).getTime()
    );

    if (previousMatchTimeDiff < nextMatchTimeDiff) {
      return lastPreviousMatch;
    }
    return nextMatch;
  }
}

export function addTime({
  time,
  hours = 0,
  days = 0,
}: {
  time: string;
  hours?: number;
  days?: number;
}) {
  const originalTimeInMilliSeconds = new Date(time).getTime();
  let updatedHours = hours;
  if (Number.isInteger(days)) {
    updatedHours = hours + days * 24;
  }

  const updatedTimeInMilliSeconds =
    originalTimeInMilliSeconds + updatedHours * 60 * 60 * 1000;

  return new Date(updatedTimeInMilliSeconds);
}

function getMatchFromMultipleMatches(
  matches: ISoccerMatchListItem[],
  overlapMatchIds: string[]
) {
  if (matches.length === 1) {
    return matches[0];
  }
  const filteredMatches = matches.filter((match) =>
    overlapMatchIds.includes(match.id)
  );
  if (filteredMatches.length) {
    return filteredMatches[0];
  }
  return matches[0];
}

function getSupportedMatchFromMultipleMatches(
  matches: ISoccerMatchListItem[],
  supportedMatchIds: string[]
) {
  const filteredSupportedMatches = matches.filter((match) =>
    supportedMatchIds.includes(match.id)
  );
  if (filteredSupportedMatches.length) return filteredSupportedMatches[0];
  return null;
}

/**
 *
 * @param matche : Played Supported Match in the last 1 day
 * @returns match/null based on below condition with reference to current dateTime
 * Note: All the match happening in a day will be showMatchResultUntil time time.
 * 1. Check if the current time is POST showMatchResultUntil hour-minute time:
 *    YES:
 *      a. If Match didn't played today (yesterday) : Remove Match from Final Matches List
 *      b. If Match Played Today, add timeTillMatchResultShown to showMatchResultUntil date-hour-minute
 *    NO:
 *      set timeTillMatchResultShown to current day 2PM
 * 2. Filtering out matches where currentTime < timeTillMatchResultShown
 * 3. Returning the first entry (last played latest match)
 */
function getPrevious1DayMatchToShow(
  match: ISoccerMatchListItem,
  nextMatch?: ISoccerMatchListItem | null,
  supportedMatchShowResultUntil?: ISupportedMatchShowResultUntil
) {
  const currentTime = new Date();

  // If there's a next match, check if we should switch to it
  if (nextMatch) {
    const nextMatchTime = new Date(nextMatch.matchDateTime);
    const timeUntilNextMatch = nextMatchTime.getTime() - currentTime.getTime();
    
    // Calculate gap between previous match end and next match start
    const currentMatch = new Date(match.matchDateTime);
    const matchDuration = 110 * 60 * 1000; // Assume 110 minutes match duration
    const currentMatchEndTime = currentMatch.getTime() + matchDuration;
    const gapBetweenMatches = nextMatchTime.getTime() - currentMatchEndTime;
    
    // Dynamic threshold based on gap between matches
    const timeGapThreshold = 40 * 60 * 1000; // 40 minutes in milliseconds
    const shortGapSwitchTime = 10 * 60 * 1000; // 10 minutes in milliseconds
    const longGapSwitchTime = 50 * 60 * 1000; // 50 minutes in milliseconds
    
    // If gap between matches is less than threshold, use short gap switch time else long gap switch time
    const switchThreshold = gapBetweenMatches <= timeGapThreshold 
      ? shortGapSwitchTime 
      : longGapSwitchTime;


    // If next match is within the switch threshold, switch to it
    if (timeUntilNextMatch <= switchThreshold && timeUntilNextMatch > 0) {
      return null; // This will cause the function to return the next match
    }
  }

  // Fallback to original logic for daily cutoff
  const utcDateTillShowResult = supportedMatchShowResultUntil?.utcDate ?? 0;
  const utcHourTillShowResult = supportedMatchShowResultUntil?.utcHour ?? 0;
  const utcMinuteTillShowResult = supportedMatchShowResultUntil?.utcMinute ?? 0;

  const currentDate = currentTime.getUTCDate();
  const currentHour = currentTime.getUTCHours();
  const currentMinute = currentTime.getUTCMinutes();
  const isPostTimeUntilShowResult =
    currentHour >= utcHourTillShowResult &&
    currentMinute >= utcMinuteTillShowResult;

  const matchStartDate = new Date(match.matchDateTime).getUTCDate();
  const isMatchPlayedToday = currentDate === matchStartDate;

  const timeTillMatchResultShown = new Date(currentTime);

  if (isPostTimeUntilShowResult) {
    if (!isMatchPlayedToday) return null;
    timeTillMatchResultShown.setUTCDate(
      timeTillMatchResultShown.getUTCDate() + utcDateTillShowResult
    );
    timeTillMatchResultShown.setUTCHours(
      utcHourTillShowResult,
      utcMinuteTillShowResult,
      0,
      0
    );
  } else {
    timeTillMatchResultShown.setUTCHours(
      utcHourTillShowResult,
      utcMinuteTillShowResult,
      0,
      0
    );
  }

  return currentTime < timeTillMatchResultShown ? match : null;
}

const ROUND_OFF_TIME_INTERVAL_MS = 10 * 60 * 1000;

/** round datetime based on interval milliseconds for eg:
 * With intervalMilliseconds as 10*60*1000 ms (10 mins):
 * - time of 2hr 26min 30s will round up to 2hr 30min 0s
 * - time of 2hr 13min 23s will round down to 2hr 10 min 0s
 * Reference: https://stackoverflow.com/a/66468325
 */
function roundDateTime(
  datetime?: Date,
  round: "up" | "down" = "up",
  intervalMilliseconds: number = ROUND_OFF_TIME_INTERVAL_MS
) {
  datetime = datetime || new Date();
  const modTicks = datetime.getTime() % intervalMilliseconds;
  let delta = modTicks === 0 ? 0 : datetime.getTime() - modTicks;
  if (round === "up") {
    delta += intervalMilliseconds;
  }
  return new Date(delta);
}

// Same priority as getDefaultMatch's supportedMatchIds branch (playing >
// recent supported within showResultUntil > next supported > last supported),
// but derived from a pre-fetched matches list — saves the 3 fan-out calls.
export function pickDefaultMatchFromList(
  matches: ISoccerMatchListItem[],
  tournamentId: string
): ISoccerMatchListItem | null {
  const tournamentConfig = appTournamentConfig[tournamentId];
  if (!tournamentConfig?.supportedMatchIds.length) return null;
  const { supportedMatchIds, supportedMatchShowResultUntil } = tournamentConfig;

  const now = Date.now();
  const oneDayAgo = now - 24 * 60 * 60 * 1000;
  const ninetyDaysFromNow = now + 90 * 24 * 60 * 60 * 1000;

  const playingMatches = matches.filter(
    (m) => m.status === SoccerMatchStatus.PLAYING
  );
  updateMultipleLiveMatchesState(playingMatches);
  if (playingMatches.length) {
    const playing = getSupportedMatchFromMultipleMatches(
      playingMatches,
      supportedMatchIds
    );
    if (playing) return playing;
  }

  const prev1Day = matches
    .filter((m) => {
      const t = new Date(m.matchDateTime).getTime();
      return t >= oneDayAgo && t <= now;
    })
    .sort(
      (a, b) =>
        new Date(b.matchDateTime).getTime() -
        new Date(a.matchDateTime).getTime()
    );
  const next90Days = matches
    .filter((m) => {
      const t = new Date(m.matchDateTime).getTime();
      return t >= now && t <= ninetyDaysFromNow;
    })
    .sort(
      (a, b) =>
        new Date(a.matchDateTime).getTime() -
        new Date(b.matchDateTime).getTime()
    );
  if (prev1Day.length) {
    const supportedPrev = getSupportedMatchFromMultipleMatches(
      prev1Day,
      supportedMatchIds
    );
    if (supportedPrev) {
      const nextSupported = getSupportedMatchFromMultipleMatches(
        next90Days,
        supportedMatchIds
      );
      const matchToShow = getPrevious1DayMatchToShow(
        supportedPrev,
        nextSupported,
        supportedMatchShowResultUntil
      );
      if (matchToShow) return matchToShow;
    }
  }

  const upcomingSupported = getSupportedMatchFromMultipleMatches(
    next90Days,
    supportedMatchIds
  );
  if (upcomingSupported) return upcomingSupported;

  const lastSupportedId = supportedMatchIds[supportedMatchIds.length - 1];
  return matches.find((m) => m.id === lastSupportedId) ?? null;
}
